<script setup lang="ts">
/**
 * Global connectivity banner. Mount once in App.vue, outside the error
 * boundary so it survives a view crash.
 *
 * Two states, offline wins over slow:
 *  - **offline**: a persistent bar at the top — the app keeps working
 *    against cache, but writes will fail until the connection returns.
 *  - **slow**: a subtler hint shown while a request has been pending
 *    past the threshold (see `network.ts`). Auto-clears when requests
 *    settle.
 *
 * `role="status"` + `aria-live="polite"` so screen readers announce the
 * change without stealing focus.
 *
 * **When NOT to use:**
 * - Per-view (it's a singleton; mounting twice produces two banners).
 * - Per-request feedback — that's `AppErrorCard` and toasts. The
 *   banner only reflects *connectivity*, not request outcome.
 */
import { computed } from 'vue'
import { CloudOff, Loader2 } from '@lucide/vue'

import { isOnline, isSlow } from '@/shared/lib/network'

const state = computed<'offline' | 'slow' | null>(() => {
  if (!isOnline.value) return 'offline'
  if (isSlow.value) return 'slow'
  return null
})
</script>

<template>
  <Transition
    enter-active-class="transition-transform duration-200 ease-out"
    enter-from-class="-translate-y-full"
    leave-active-class="transition-transform duration-150 ease-in"
    leave-to-class="-translate-y-full"
  >
    <div
      v-if="state"
      role="status"
      aria-live="polite"
      class="fixed inset-x-0 top-0 z-70 flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium shadow-sm safe-top"
      :class="
        state === 'offline'
          ? 'bg-destructive text-destructive-foreground'
          : 'bg-yellow-500 text-yellow-950'
      "
    >
      <template v-if="state === 'offline'">
        <CloudOff class="size-4 shrink-0" aria-hidden="true" />
        <span>You’re offline — changes can’t be saved until the connection returns.</span>
      </template>
      <template v-else>
        <Loader2 class="size-4 shrink-0 animate-spin" aria-hidden="true" />
        <span>Slow connection — still working…</span>
      </template>
    </div>
  </Transition>
</template>
